import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

/**
 * Handles the return leg of the Emergent-managed Google OAuth flow.
 *
 * The redirect from `auth.emergentagent.com` lands us at
 * `<origin>/admin/dashboard#session_id=<sid>` (any path — the router
 * intercepts on the fragment). We synchronously read the fragment,
 * exchange the session_id for a Piczoo JWT via the backend, and then
 * navigate to the dashboard with a clean URL.
 *
 * Per the Emergent playbook, we use `useRef` (not state) as the
 * "already processed" guard so React StrictMode's double-invocation
 * of effects doesn't call the endpoint twice.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithSessionId } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      // Fragment format: #session_id=<sid> (optionally with other pairs).
      const hash = window.location.hash?.replace(/^#/, '') || '';
      const params = new URLSearchParams(hash);
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setError('No session id in callback URL. Please try signing in again.');
        return;
      }

      try {
        await loginWithSessionId(sessionId);
        // Clean the fragment so a refresh doesn't retry.
        window.history.replaceState({}, document.title, '/admin/dashboard');
        navigate('/admin/dashboard', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Sign-in failed. Please try again.';
        setError(detail);
        toast.error(detail);
      }
    };

    run();
  }, [loginWithSessionId, navigate]);

  return (
    <div
      className="min-h-screen bg-noir-900 text-white flex items-center justify-center p-6"
      data-testid="auth-callback-page"
    >
      <div className="max-w-md w-full text-center">
        {error ? (
          <>
            <div className="text-piczoo-300 text-2xl font-semibold mb-3">Sign-in blocked</div>
            <p className="text-white/70 mb-6 text-sm" data-testid="auth-callback-error">
              {error}
            </p>
            <button
              onClick={() => navigate('/admin/login', { replace: true })}
              className="pink-btn px-6 py-2.5 rounded-full text-sm"
              data-testid="auth-callback-retry-btn"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-piczoo-400 mx-auto mb-4 animate-spin" />
            <div className="text-lg font-medium">Signing you in…</div>
            <p className="text-white/50 text-sm mt-2">
              Verifying your Google account with the Piczoo admin panel.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
